import React, { useEffect, useState, useCallback } from 'react';
import palettes from '../data/palettes.json';
import fonts from '../data/fonts.json';
import './StylesRight.css';
import { getCustomPalette } from '../services/customPalettes';

const StylesRight = ({ userChoices }) => {
  const { device, theme, palette: paletteName, font: fontName, isCustomPalette, customPaletteId } = userChoices;
  const [customPalette, setCustomPalette] = useState(null);
  const [loading, setLoading] = useState(false);

  // Create a stable reference to the fetch function
  const fetchCustomPalette = useCallback(async (paletteId) => {
    setLoading(true);
    try {
      const palette = await getCustomPalette(paletteId);
      if (palette && palette.palette) {
        setCustomPalette(palette.palette);
      }
    } catch (error) {
      console.error('Error fetching custom palette:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch custom palette when needed
  useEffect(() => {
    if (isCustomPalette && customPaletteId) {
      fetchCustomPalette(customPaletteId);
    } else {
      setCustomPalette(null);
    }
  }, [isCustomPalette, customPaletteId, fetchCustomPalette, userChoices.lastUpdated]); // Add lastUpdated to dependencies

  // Clear custom palette when switching away from custom palettes
  useEffect(() => {
    if (!isCustomPalette) {
      setCustomPalette(null);
    }
  }, [isCustomPalette]);

  // Find the standard palette if not using a custom one
  const standardPalette = !isCustomPalette ? 
    palettes[theme]?.find(p => p.name === paletteName) : null;
  
  // Use custom palette if available, otherwise use standard
  const effectivePalette = customPalette || standardPalette;
  
  // Fallback to first palette if no matching palette found
  const fallbackPalette = palettes[theme]?.[0];
  
  // Final palette to use
  const palette = effectivePalette || fallbackPalette;
  
  // Find the font
  const font = fonts.find(f => f.name === fontName) || fonts[0];

  // Apply palette and font to CSS variables
  useEffect(() => {
    if (!palette || !font) return;
    
    const root = document.documentElement;

    // Apply selected font family
    root.style.setProperty('--preview-font-family', font.family);

    // Apply selected colors to CSS variables
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-secondary', palette.secondary);
    root.style.setProperty('--color-accent', palette.accent);
    root.style.setProperty('--bg-color', theme === 'light' ? '#ffffff' : '#1f2937');
    root.style.setProperty('--base-text-color', theme === 'light' ? '#000000' : '#ffffff');
    root.style.setProperty('--on-primary', theme === 'light' ? '#ffffff' : '#000000');
    root.style.setProperty('--on-secondary', theme === 'light' ? '#ffffff' : '#000000');
    root.style.setProperty('--on-accent', theme === 'light' ? '#ffffff' : '#000000');
  }, [palette, font, theme]);

  // If loading, show loading state
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4 bg-white">
        <p className="text-gray-500">Loading style preview...</p>
      </div>
    );
  }

  // Show a warning message if the user selected custom palette tab but hasn't created any
  if (isCustomPalette && !customPaletteId) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4 bg-white">
        <div className="p-8 bg-gray-100 rounded-md text-center max-w-md">
          <p className="text-gray-500">Custom styles can be seen here after they are created.</p>
          <p className="text-gray-500 mt-2">Click "Create New Palette" to get started.</p>
        </div>
      </div>
    );
  }

  // If missing essential data, show appropriate message
  if (!palette || !font) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4 bg-white">
        <p className="text-gray-500">Missing style information.</p>
      </div>
    );
  }

  // Adjust mobile preview width and add transition
  const previewClasses = `overflow-y-auto w-full h-full rounded transition-all duration-500 ease-in-out ${
    device === 'Mobile' ? 'max-w-sm' : 'w-full'
  } ${
    theme === 'light' ? 'border border-gray-300' : 'border border-gray-700'
  }`;

  // Adjust font sizes based on device
  const headingSize = device === 'Mobile' ? 'text-2xl' : 'text-3xl';
  const subHeadingSize = device === 'Mobile' ? 'text-lg' : 'text-2xl';
  const textSize = device === 'Mobile' ? 'text-sm' : 'text-base';

  return (
    <div className="w-full h-full flex items-center justify-center p-4 bg-white">
      <div
        className={previewClasses}
        style={{
          backgroundColor: 'var(--bg-color)',
          color: 'var(--base-text-color)',
          fontFamily: 'var(--preview-font-family)',
        }}
      >
        <div className="p-4">
          <h1
            className={`${headingSize} font-bold mb-4 text-left`}
            style={{ color: 'var(--color-primary)' }}
          >
            {isCustomPalette ? 'Custom Palette' : 'Palette'} in Action
          </h1>

          {/* Color Preview Grid */}
          <section className="mb-8">
            <h2 className={`${subHeadingSize} font-bold mb-4 text-left`}>Color Preview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className="p-4 rounded flex items-center justify-center text-center"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--on-primary)' }}
              >
                Primary
              </div>
              <div
                className="p-4 rounded flex items-center justify-center text-center"
                style={{ backgroundColor: 'var(--color-secondary)', color: 'var(--on-secondary)' }}
              >
                Secondary
              </div>
              <div
                className="p-4 rounded flex items-center justify-center text-center"
                style={{ backgroundColor: 'var(--color-accent)', color: 'var(--on-accent)' }}
              >
                Accent
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <section className="mb-8 sm:max-w-xs">
            <h2 className={`${subHeadingSize} font-bold mb-4 text-left`}>Action Buttons</h2>
            <div className="grid grid-cols-2 gap-2">
              <button
                className={`px-2 py-2 rounded font-semibold ${textSize} w-full`}
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--on-primary)',
                }}
              >
                Primary
              </button>
              <button
                className={`px-2 py-2 rounded font-semibold ${textSize} w-full`}
                style={{
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--on-secondary)',
                }}
              >
                Secondary
              </button>
            </div>
          </section>

          {/* Card Preview */}
          <section className="mb-8">
            <h2 className={`${subHeadingSize} font-bold mb-4 text-left`}>Card Preview</h2>
            <div
              className="rounded shadow-lg overflow-hidden"
              style={{ border: '1px solid var(--color-primary)' }}
            >
              <div
                className="p-4"
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--on-primary)' }}
              >
                <h3 className={`${textSize} font-bold`}>Card Header</h3>
              </div>
              <div
                className="p-4"
                style={{ backgroundColor: 'var(--bg-color)', color: 'var(--base-text-color)' }}
              >
                <p className={textSize}>
                  This card demonstrates usage of the primary color in the header along with an accent badge.
                </p>
                <span
                  className="inline-block mt-4 px-3 py-1 rounded"
                  style={{ backgroundColor: 'var(--color-accent)', color: 'var(--on-accent)' }}
                >
                  Accent Badge
                </span>
              </div>
            </div>
          </section>

          {/* Form Elements */}
          <section className="mb-8">
            <h2 className={`${subHeadingSize} font-bold mb-4 text-left`}>Form Elements</h2>
            <p className={textSize}>Click on input fields to see accent color border.</p>
            <form className="mt-4 space-y-4">
              <div>
                <label htmlFor="name" className={`block mb-1 ${textSize}`}>Name</label>
                <input
                  type="text"
                  id="name"
                  className="p-2 border rounded w-full focus:outline-none focus:border-accent"
                  style={{
                    borderColor: 'var(--base-text-color)',
                    color: 'var(--base-text-color)',
                    backgroundColor: 'transparent',
                  }}
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label htmlFor="email" className={`block mb-1 ${textSize}`}>Email</label>
                <input
                  type="email"
                  id="email"
                  className="p-2 border rounded w-full focus:outline-none focus:border-accent"
                  style={{
                    borderColor: 'var(--base-text-color)',
                    color: 'var(--base-text-color)',
                    backgroundColor: 'transparent',
                  }}
                  placeholder="Enter your email"
                />
              </div>
              <button
                type="submit"
                className={`px-4 py-2 rounded font-semibold ${textSize}`}
                style={{ backgroundColor: 'var(--color-primary)', color: 'var(--on-primary)' }}
              >
                Submit
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StylesRight;